import { PracticePreset } from "features/onboarding/guided/PracticeBotPreset";
import { PreserveBotFormValuesContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useCallback, useContext, useLayoutEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { stepTitle } from "features/onboarding/common/onboardingUi";
import { BOT_TYPE_TO_BASE_PATH } from "features/bots/common/constants";
import BotForm from "features/bots/BotForm/BotForm";
import { BotData, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { TradingIntent } from "features/onboarding/guided/intent";
import {
  HeadingBlock,
  Eyebrow,
  SectionTitle,
  Lead,
} from "features/onboarding/guided/GuidedWelcome";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { ONBOARDING_SETUP_ID } from "testIds";
import { useOnboardingStepView } from "analytics/onboarding";
import useMeasure from "utils/hooks/useMeasure";
const PHONE_QUERY = "(max-width: 560px)";
const MIN_PHONE_EDITOR = 360;
/** The onboarding content's bottom padding (OnboardingProvider). */
const PAGE_BOTTOM_PADDING = 20;

const scrollerOf = (element: HTMLElement): HTMLElement => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    )
      return node;
  }
  return (document.scrollingElement as HTMLElement) || document.documentElement;
};

/**
 * Phones: the form takes exactly the rest of the screen, so the page itself
 * doesn't scroll and the form's action row sits on the bottom edge (the form
 * scrolls inside, like a native screen).
 */
const usePhoneFillHeight = (
  editor: HTMLElement | null,
  enabled: boolean
): number | undefined => {
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    if (!editor || !enabled || typeof ResizeObserver === "undefined") {
      setHeight(undefined);
      return;
    }
    const sync = () => {
      if (!window.matchMedia(PHONE_QUERY).matches) return setHeight(undefined);
      const scroller = scrollerOf(editor);
      const isPage = scroller === document.scrollingElement;
      const rect = editor.getBoundingClientRect();
      // Positions in the scroller's content, so scrolling doesn't change them.
      const top = isPage
        ? rect.top + window.scrollY
        : rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      // What follows the form inside the scroller: its ancestors' bottom
      // padding and margins and their later siblings (not the free space a
      // min-height leaves).
      let after = 0;
      for (
        let node: HTMLElement = editor;
        node.parentElement && node !== scroller;
        node = node.parentElement
      ) {
        const style = getComputedStyle(node);
        after += parseFloat(style.marginBottom) || 0;
        if (node !== editor)
          after +=
            (parseFloat(style.paddingBottom) || 0) +
            (parseFloat(style.borderBottomWidth) || 0);
        for (
          let sibling = node.nextElementSibling;
          sibling;
          sibling = sibling.nextElementSibling
        ) {
          const siblingStyle = getComputedStyle(sibling);
          if (["fixed", "absolute"].includes(siblingStyle.position)) continue;
          after +=
            sibling.getBoundingClientRect().height +
            (parseFloat(siblingStyle.marginTop) || 0) +
            (parseFloat(siblingStyle.marginBottom) || 0);
        }
        if (node.parentElement === scroller) break;
      }
      const next = Math.max(
        MIN_PHONE_EDITOR,
        Math.floor(
          (isPage ? window.innerHeight : scroller.clientHeight) -
            top -
            Math.max(0, after)
        )
      );
      setHeight((current) =>
        current !== undefined && Math.abs(current - next) < 2 ? current : next
      );
    };
    const observer = new ResizeObserver(sync);
    observer.observe(document.body);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [editor, enabled]);
  return height;
};

const OnboardingBotSetup = ({ intent }: { intent: TradingIntent }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  useOnboardingStepView("bot_setup");
  const [headingRef, { height: headingHeight }] = useMeasure<HTMLDivElement>();
  const demo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  // Demo setups always start from the practice defaults (symbol, name, amount,
  // grids), so "build my own" opens a ready form instead of an empty one with
  // validation errors; every value stays editable.
  // A DCA preset being customized copies that bot instead (botUUID).
  const location = useLocation();
  const copyingPreset = !!new URLSearchParams(location.search).get("botUUID");
  const practicePreset: PracticePreset | undefined =
    demo &&
    (intent === "dca" ||
      intent === "grid" ||
      intent === "tradingview" ||
      intent === "hedging") &&
    !copyingPreset
      ? intent
      : undefined;
  const { onFinishOnboarding } = useContext(OnboardingContext)!;
  // Confirm mode: the walkthrough's main button (and a preset) asks to start
  // the practice bot as set up; the form stays one tap away ("Customize").
  const history = useHistory();
  // (Only DCA and Grid have a prepared bot to confirm.)
  const confirming =
    (practicePreset === "dca" || practicePreset === "grid") &&
    new URLSearchParams(location.search).get("preset") === intent;
  const [confirmSlot, setConfirmSlot] = useState<HTMLElement | null>(null);
  // The confirmation waits for the practice preset (range, grids, amount),
  // so it never shows the form's own defaults first.
  const [presetApplied, setPresetApplied] = useState(false);
  const onPresetReady = useCallback(() => setPresetApplied(true), []);
  const [editor, setEditor] = useState<HTMLDivElement | null>(null);
  const phoneHeight = usePhoneFillHeight(editor, !confirming);
  const onCustomize = () => {
    const query = new URLSearchParams(location.search);
    query.set("preset", "");
    // Same route: the form stays mounted with its values.
    history.replace({ ...location, search: query.toString() });
  };
  const viewBot = (created?: BotData): void => {
    if (created?.id)
      onFinishOnboarding(BOT_TYPE_TO_BASE_PATH[created.bot_type]);
  };
  return (
    <OnboardingBotSetupContext.Provider
      value={{
        onCreated: viewBot,
        confirming,
        confirmSlot,
        onCustomize,
        presetReady: !practicePreset || presetApplied,
        onPresetReady,
      }}
    >
      <SetupWrapper data-testid={ONBOARDING_SETUP_ID}>
        <HeadingBlock ref={headingRef}>
          <Eyebrow>
            {t(demo ? "guided.virtual_only" : "intent.live_setup")}
          </Eyebrow>
          <SetupTitle>
            {confirming
              ? t("intent.confirm_title")
              : t("revision.setup_title", {
                  method: t(`intent.${intent}.name`),
                })}
          </SetupTitle>
          <SetupLead $hideOnPhone={!confirming}>
            {t(
              confirming
                ? "intent.confirm_description"
                : demo
                ? "revision.demo_setup_description"
                : "revision.setup_description"
            )}
          </SetupLead>
        </HeadingBlock>
        {confirming && <ConfirmSlot ref={setConfirmSlot} />}
        <Editor
          ref={setEditor}
          // Over the page's bottom padding too: the form's own action row
          // brings the space under its buttons.
          style={
            phoneHeight
              ? { height: phoneHeight, marginBottom: -PAGE_BOTTOM_PADDING }
              : undefined
          }
          $headingHeight={headingHeight || 140}
          $hidden={confirming}
          aria-hidden={confirming || undefined}
        >
          <PreserveBotFormValuesContext.Provider value={true}>
            <BotForm practicePreset={practicePreset} />
          </PreserveBotFormValuesContext.Provider>
        </Editor>
      </SetupWrapper>
    </OnboardingBotSetupContext.Provider>
  );
};
export default OnboardingBotSetup;
const SetupTitle = styled(SectionTitle)`
  ${stepTitle};
  margin: 8px 0 4px;
`;
/* Phones: the journey line and the title say enough over the form. */
const SetupLead = styled(Lead)<{ $hideOnPhone?: boolean }>`
  @media ${PHONE_QUERY} {
    ${(p) => p.$hideOnPhone && "display: none;"}
  }
`;
/* The confirm card keeps the steps' 1040px column (the back link's, the
   preset review's) rather than the form's wider one. */
const ConfirmSlot = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
`;
const SetupWrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 1120px;
  margin: 0 auto;
`;
// Confirm mode keeps the form mounted (its defaults and creation path run)
// but out of sight and out of reach.
const Editor = styled.div<{ $headingHeight: number; $hidden?: boolean }>`
  ${(p) =>
    p.$hidden &&
    `
    /* Fixed, so its height never makes a scroller of the page. */
    position: fixed !important;
    top: 0;
    left: -10000px;
    width: 360px !important;
    visibility: hidden;
    pointer-events: none;
  `}
  height: max(400px, calc(100dvh - ${(p) => p.$headingHeight + 260}px));
  min-height: 0;
  width: 100%;
  position: relative;
  @media (max-width: 650px) {
    height: max(360px, calc(100dvh - ${(p) => p.$headingHeight + 230}px));
  }
  /* Phones: the header, back link, journey line and the explore link take
     about 170px besides the heading; the form gets the rest. */
  @media (max-width: 560px) {
    height: max(360px, calc(100dvh - ${(p) => p.$headingHeight + 170}px));
    /* The form brings its own 16px padding: line its fields up with the
       heading at the page's gutter. */
    width: auto;
    margin: 0 -16px;
  }
`;
